import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import PublicPageShell from '../components/PublicPageShell';
import { useLanguage } from '../contexts/LanguageContext';
import { getLocalizedPublicServices, servicePath } from '../data/contentLibrary';

const servicesPageCopy = {
  en: {
    h1: 'Website services built around business workflows',
    intro:
      'Webouse plans, designs and builds websites as public business systems: service pages, booking paths, lead journeys, SEO-ready content and maintainable technical foundations. Each service below explains fit, deliverables, limits, pricing approach and related next steps.',
  },
  tr: {
    h1: 'İş akışlarına göre tasarlanan web sitesi hizmetleri',
    intro:
      'Webouse web sitelerini yalnızca vitrin olarak değil, çalışan iş sistemleri olarak planlar, tasarlar ve geliştirir: hizmet sayfaları, randevu yolları, lead yolculukları, SEO uyumlu içerik ve sürdürülebilir teknik altyapı. Aşağıdaki her hizmet uygunluğu, çıktıları, sınırları, fiyatlandırma yaklaşımını ve sonraki adımları açıklar.',
  },
};

const ServicesPage = () => {
  const { currentLang, t } = useLanguage();
  const copy = servicesPageCopy[currentLang] || servicesPageCopy.en;
  const services = getLocalizedPublicServices(currentLang);

  return (
    <PublicPageShell mainClassName="pt-20">
      <section className="relative overflow-hidden bg-black">
        <div className="hero-grid absolute inset-0 pointer-events-none" />
        <div className="relative mx-auto max-w-[1240px] px-5 py-16 md:px-[7.6923%] md:py-24">
          <nav className="mb-8 flex flex-wrap gap-2 text-sm text-white/44" aria-label={t('common.breadcrumb')}>
            <Link to="/" className="hover:text-[#00FFD1]">{t('nav.home')}</Link>
            <span>/</span>
            <span className="text-white/70">{t('nav.services')}</span>
          </nav>

          <div className="max-w-4xl">
            <p className="mb-4 text-sm font-semibold uppercase text-[#00FFD1]">
              {t('common.servicesEyebrow')}
            </p>
            <h1 className="text-4xl font-semibold leading-tight text-white md:text-6xl">
              {copy.h1}
            </h1>
            <p className="mt-6 text-base leading-8 text-white/64 md:text-lg">
              {copy.intro}
            </p>
          </div>

          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {services.map((service) => (
              <Link
                key={service.slug}
                to={servicePath(service.slug)}
                className="group rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 transition-all hover:-translate-y-1 hover:border-[#00FFD1]/35 hover:bg-[#00FFD1]/[0.035]"
              >
                <div className="mb-5 flex items-start justify-between gap-4">
                  <CheckCircle2 className="h-6 w-6 flex-none text-[#00FFD1]" />
                  <ArrowRight className="h-5 w-5 flex-none text-white/35 transition-transform group-hover:translate-x-1 group-hover:text-[#00FFD1]" />
                </div>
                <h2 className="text-2xl font-semibold text-white">{service.title}</h2>
                <p className="mt-3 text-sm leading-6 text-white/58">{service.excerpt}</p>
                <p className="mt-5 text-sm font-semibold text-[#00FFD1]">
                  {t('common.viewServiceDetails')}
                </p>
              </Link>
            ))}
          </div>

          <div className="mt-14 flex flex-col gap-3 border-t border-white/[0.08] pt-8 sm:flex-row">
            <Link to="/book-call" className="btn-glass-primary">
              {t('nav.bookConsultation')}
            </Link>
            <Link to="/resources" className="btn-glass-secondary">
              {t('common.readResources')}
            </Link>
          </div>
        </div>
      </section>
    </PublicPageShell>
  );
};

export default ServicesPage;
